import { useState, useEffect, useCallback } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import LIBRARIES from "@/lib/libraryConfig";
import { listLibrary, deleteLibraryItem } from "@/apiClient";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from "@/components/ui/table";
import LibraryFormDialog from "@/components/LibraryFormDialog";
import RestaurantFormDialog from "@/components/RestaurantFormDialog";
import DeleteConfirmDialog from "@/components/DeleteConfirmDialog";
import { Plus, Pencil, Trash2, Loader2, RefreshCw, Copy, Printer, ArrowRightLeft, ListPlus } from "lucide-react";
import ImportFromLiveStationsDialog from "@/components/ImportFromLiveStationsDialog";
import { toast } from "sonner";
import { copyLiveStationToCrewMess, duplicateLibItem, listVoyageLiveStationUsage } from "@/api/mealPlanApi";
import { cn } from "@/lib/utils";

// v8 — libraries that have items shown on the /print/stations A4 PDF
// handout page. We surface a one-click "Print A4 Guide" shortcut in the
// header of these library pages so the user doesn't have to hunt for
// the global Print Stations nav entry. Maps each library to the
// corresponding section anchor on /print/stations.
const PRINT_STATIONS_LIBS = {
  theme_buffets: "lib_theme_buffets",
  live_stations: "lib_live_stations",
  standard_stations: "lib_standard_stations",
  crew_mess_live_stations: "lib_crew_mess_live_stations",
};

// Phase 7++++ — libraries whose form needs the auxiliary lists (materials
// dropdown, live/standard station pickers). For everything else the
// dialog is fine with the default empty arrays.
const AUX_LIBS_NEEDED = new Set([
  "theme_buffets",
  "live_stations",
  "standard_stations",
  // Phase 7+/Crew-Mess-Buffet — Crew Mess Live Stations also need
  // the Materials list for the per-dish Material picker.
  "crew_mess_live_stations",
  // Phase 9 — Canapé form needs buffet_materials (serving material),
  // production_areas (production area), staff (person in charge).
  "canapes",
  // Phase 12 — Special Events form needs the new groups lib.
  "special_events",
]);

/** Format a value that may be either List[str] (legacy) or List[Dish]
 *  (Turn 2). Used for buffet-context dish columns in the library list.
 */
function formatDishyList(value) {
  if (!Array.isArray(value) || value.length === 0) return "—";
  const labels = value
    .map((d) => {
      if (typeof d === "string") return d;
      if (d && typeof d === "object") {
        const name = (d.name || "").trim();
        if (!name) return null;
        const mat = (d.material_name || "").trim();
        return mat ? `${name} (${mat})` : name;
      }
      return null;
    })
    .filter(Boolean);
  return labels.length ? labels.join(", ") : "—";
}

/** Format a cell value for display in the library list table. */
function formatCellValue(value, key, libraryName) {
  if (value === null || value === undefined) return "—";

  // Phase 5 column renderers.
  if (libraryName === "restaurants" && key === "type") {
    return (
      <Badge
        variant="outline"
        className={cn(
          "uppercase tracking-wide",
          value === "specialty"
            ? "border-amber-300 bg-amber-50 text-amber-900"
            : "border-sky-300 bg-sky-50 text-sky-900"
        )}
      >
        {String(value)}
      </Badge>
    );
  }
  if (
    libraryName === "theme_buffets" &&
    (key === "hot_kitchen" || key === "cold_kitchen" || key === "pastry")
  ) {
    const count = Array.isArray(value) ? value.length : 0;
    const cls =
      key === "pastry"
        ? "border-yellow-300 bg-yellow-50 text-yellow-900"
        : key === "cold_kitchen"
          ? "border-cyan-300 bg-cyan-50 text-cyan-900"
          : "border-orange-300 bg-orange-50 text-orange-900";
    return (
      <Badge variant="outline" className={cls}>
        {count} {count === 1 ? "item" : "items"}
      </Badge>
    );
  }
  if (libraryName === "theme_buffets" && key === "lines") {
    const lines = Array.isArray(value) ? value : [];
    if (lines.length === 0)
      return <span className="text-xs text-muted-foreground">—</span>;
    const totalItems = lines.reduce(
      (acc, ln) => acc + ((ln.items || []).length || 0),
      0,
    );
    return (
      <Badge
        variant="outline"
        className="border-emerald-300 bg-emerald-50 text-emerald-900"
      >
        {lines.length} line{lines.length === 1 ? "" : "s"} · {totalItems} item
        {totalItems === 1 ? "" : "s"}
      </Badge>
    );
  }
  // Phase 7++++ — live/standard station "dishes" column now holds Dish
  // objects, not flat strings. Render compactly with material in parens.
  if (
    (libraryName === "live_stations"
      || libraryName === "standard_stations"
      // Phase 7+/Crew-Mess-Buffet — same renderer for the parallel
      // crew_mess library. Was rendering `[object Object]` because this
      // branch didn't list the new library key.
      || libraryName === "crew_mess_live_stations") &&
    key === "dishes"
  ) {
    return (
      <span className="text-xs text-muted-foreground line-clamp-2">
        {formatDishyList(value)}
      </span>
    );
  }
  if (
    libraryName === "crew_mess_themes" &&
    (key === "breakfast" || key === "lunch" || key === "dinner")
  ) {
    const count = Array.isArray(value) ? value.length : 0;
    return (
      <span className="text-xs text-muted-foreground">
        {count} item{count === 1 ? "" : "s"}
      </span>
    );
  }
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) {
    if (value.length === 0) return "—";
    return value.join(", ");
  }
  if (key === "repeat_rule" && typeof value === "object") {
    const label = value.type?.replace(/_/g, " ");
    if (value.type === "specific_days" && value.days) {
      return `${label} (days: ${value.days.join(", ")})`;
    }
    return label || "—";
  }
  return String(value);
}

export default function LibraryPage() {
  const { libraryName } = useParams();
  const config = LIBRARIES[libraryName];

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Dialog state
  const [formOpen, setFormOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletingItem, setDeletingItem] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Phase 7++++ — auxiliary libs used by `dishList` / `stations` /
  // `buffetLines` renderers inside LibraryFormDialog. Loaded lazily only
  // when the current library actually needs them, so editing a Ship or
  // a Target stays a zero-overhead operation.
  const [materialsLib, setMaterialsLib] = useState([]);
  const [liveStationsLib, setLiveStationsLib] = useState([]);
  const [standardStationsLib, setStandardStationsLib] = useState([]);
  // Phase 9 — Canapé form needs production_areas + staff libraries too.
  const [productionAreasLib, setProductionAreasLib] = useState([]);
  const [staffLib, setStaffLib] = useState([]);
  // Phase 12 — Special Events form pulls from this lib for its Group dropdown.
  const [specialEventGroupsLib, setSpecialEventGroupsLib] = useState([]);
  // Phase 15 (F1) — Special Events form has a new "Linked Canapés"
  // multi-select sourced from lib_canapes.
  const [canapesLib, setCanapesLib] = useState([]);

  useEffect(() => {
    if (!libraryName || !AUX_LIBS_NEEDED.has(libraryName)) {
      // No buffet UX → skip the extra fetches entirely.
      return;
    }
    let cancelled = false;
    Promise.all([
      listLibrary("buffet_materials").catch(() => []),
      listLibrary("live_stations").catch(() => []),
      listLibrary("standard_stations").catch(() => []),
      // Phase 9 — only fetch when canapé form is the target. The two
      // libs are tiny so we could always fetch them, but keeping the
      // request count low for non-canapé libraries is a free win.
      libraryName === "canapes"
        ? listLibrary("production_areas").catch(() => [])
        : Promise.resolve([]),
      libraryName === "canapes"
        ? listLibrary("staff").catch(() => [])
        : Promise.resolve([]),
      // Phase 12 — only fetch the groups library when the user is
      // editing a special_events row (the only form that needs it).
      libraryName === "special_events"
        ? listLibrary("special_event_groups").catch(() => [])
        : Promise.resolve([]),
      // Phase 15 (F1) — canapes lib for the linked-canapés multi-select
      // on the Special Events form.
      libraryName === "special_events"
        ? listLibrary("canapes").catch(() => [])
        : Promise.resolve([]),
    ]).then(([mats, lives, stds, areas, staff, segs, canapes]) => {
      if (cancelled) return;
      setMaterialsLib(mats || []);
      setLiveStationsLib(lives || []);
      setStandardStationsLib(stds || []);
      setProductionAreasLib(areas || []);
      setStaffLib(staff || []);
      setSpecialEventGroupsLib(segs || []);
      setCanapesLib(canapes || []);
    });
    return () => {
      cancelled = true;
    };
    // `listLibrary` is a module-scoped import — stable reference,
    // intentionally not in the dep array (ESLint correctly treats it
    // as an outer-scope value that doesn't drive re-renders).
  }, [libraryName]);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listLibrary(libraryName);
      setItems(data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Failed to load");
    } finally {
      setLoading(false);
    }
    // Same: `listLibrary` is a stable module import.
  }, [libraryName]);

  useEffect(() => {
    if (config) fetchItems();
  }, [config, fetchItems]);

  // Phase 15 (F2) — open state for the "Import from Live Stations
  // Library" picker. Only mounted when libraryName === "crew_mess_live_stations".
  const [importFromLSOpen, setImportFromLSOpen] = useState(false);

  if (!config) {
    return <Navigate to="/" replace />;
  }

  const Icon = config.icon;

  const handleCreate = () => {
    setEditingItem(null);
    setFormOpen(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormOpen(true);
  };

  const handleDeleteClick = (item) => {
    setDeletingItem(item);
    setDeleteOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingItem) return;
    setDeleteLoading(true);
    try {
      await deleteLibraryItem(libraryName, deletingItem.id);
      setDeleteOpen(false);
      setDeletingItem(null);
      await fetchItems();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Delete failed");
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleFormSuccess = () => {
    setFormOpen(false);
    setEditingItem(null);
    fetchItems();
  };

  // Per-library test-id aliases so QA can hit `add-<lib>-btn` etc.
  const addBtnTestId = `add-${libraryName}-btn`;
  const itemTestId = (id) => `item-${libraryName}-${id}`;
  const editTestId = (id) => `edit-${libraryName}-${id}`;
  const deleteTestId = (id) => `delete-${libraryName}-${id}`;
  const singular = (config.addLabel || `Add ${config.label}`).replace(
    /^Add\s+/i,
    ""
  );

  return (
    <div className="px-6 py-8" data-testid={`library-page-${libraryName}`}>
      {/* Header */}
      <div className="mb-6 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Icon className="h-5 w-5" />
          </div>
          <div>
            <h1
              className="text-xl font-bold tracking-tight"
              data-testid="library-title"
            >
              {config.label}
            </h1>
            <p className="text-sm text-muted-foreground">{config.description}</p>
          </div>
        </div>
        <div className="mt-3 flex items-center gap-2 sm:mt-0">
          <Button
            variant="outline"
            size="sm"
            onClick={fetchItems}
            disabled={loading}
            data-testid="library-refresh-btn"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          {PRINT_STATIONS_LIBS[libraryName] && (
            // v8 — shortcut to the global Print Stations page, anchored on
            // this library's accordion section. Saves the user from having
            // to open the sidebar nav.
            <Button
              variant="outline"
              size="sm"
              asChild
              data-testid="library-print-stations-btn"
            >
              <Link to={`/print/stations#${PRINT_STATIONS_LIBS[libraryName]}`}>
                <Printer className="h-4 w-4" />
                Print A4 Guide
              </Link>
            </Button>
          )}
          {libraryName === "crew_mess_live_stations" && (
            // Phase 15 (F2) — open the "Import from Live Stations
            // Library" picker. Snapshot copy, no link to the source.
            <Button
              variant="outline"
              size="sm"
              onClick={() => setImportFromLSOpen(true)}
              data-testid="import-from-live-stations-btn"
            >
              <ListPlus className="h-4 w-4" />
              Add from Live Stations Library
            </Button>
          )}
          <Button size="sm" onClick={handleCreate} data-testid={addBtnTestId}>
            <Plus className="h-4 w-4" />
            {config.addLabel || `Add ${singular}`}
          </Button>
        </div>
      </div>

      {/* Item count badge */}
      {!loading && (
        <div className="mb-4">
          <Badge variant="secondary" data-testid="library-item-count">
            {items.length} item{items.length !== 1 ? "s" : ""}
          </Badge>
        </div>
      )}

      {error && (
        <div
          className="mb-4 rounded-md border border-destructive/50 bg-destructive/5 p-3 text-sm text-destructive"
          data-testid="library-error"
        >
          {error}
        </div>
      )}

      {loading && (
        <div className="flex items-center justify-center py-20" data-testid="library-loading">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      )}

      {!loading && items.length === 0 && !error && (
        <div
          className="flex flex-col items-center justify-center rounded-lg border border-dashed py-16 text-center"
          data-testid="library-empty"
        >
          <Icon className="mb-3 h-10 w-10 text-muted-foreground/40" />
          <p className="text-sm text-muted-foreground">
            No {config.label.toLowerCase()} yet.
          </p>
          <Button
            size="sm"
            className="mt-4"
            onClick={handleCreate}
            data-testid={`${addBtnTestId}-empty`}
          >
            <Plus className="h-4 w-4" />
            Add first item
          </Button>
        </div>
      )}

      {!loading && items.length > 0 && (
        <div className="rounded-lg border" data-testid="library-table">
          <Table>
            <TableHeader>
              <TableRow>
                {config.columns.map((col) => {
                  const field = config.fields.find((f) => f.key === col);
                  return (
                    <TableHead key={col}>
                      {field?.label || col}
                    </TableHead>
                  );
                })}
                <TableHead className="w-[100px] text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.id} data-testid={itemTestId(item.id)}>
                  {config.columns.map((col) => (
                    <TableCell key={col}>
                      <span className="line-clamp-2">
                        {formatCellValue(item[col], col, libraryName)}
                      </span>
                    </TableCell>
                  ))}
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      {/* Phase 15 (B1) — In-place Duplicate. Always
                          available on every library section. Copies
                          the doc into the SAME collection with an
                          auto-suffixed " (Copy)" name. */}
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={async () => {
                          try {
                            const dup = await duplicateLibItem(
                              libraryName, item.id,
                            );
                            toast.success(`Duplicated as "${dup.name}"`);
                            // Refresh the table.
                            await fetchItems();
                          } catch (e) {
                            toast.error(
                              e?.response?.data?.detail || "Duplicate failed",
                            );
                          }
                        }}
                        data-testid={`duplicate-${item.id}`}
                        aria-label={`Duplicate ${item.name}`}
                        title="Duplicate"
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                      {libraryName === "live_stations" && (
                        // Phase 7+/Crew-Mess-Buffet — sideways arrow
                        // icon (ArrowRightLeft) clearly distinguishes
                        // this cross-library copy from the in-place
                        // Duplicate icon above. Source entry stays in
                        // lib_live_stations; a snapshot lands in
                        // lib_crew_mess_live_stations.
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={async () => {
                            try {
                              const dup = await copyLiveStationToCrewMess(item.id);
                              toast.success(
                                `Copied "${dup.name}" to Crew Mess Live Stations`,
                              );
                            } catch (e) {
                              toast.error(
                                e?.response?.data?.detail || "Copy failed",
                              );
                            }
                          }}
                          data-testid={`copy-to-crew-mess-${item.id}`}
                          aria-label={`Copy ${item.name} to Crew Mess Live Stations`}
                          title="Copy to Crew Mess Live Stations →"
                          className="text-indigo-600 hover:bg-indigo-50"
                        >
                          <ArrowRightLeft className="h-4 w-4" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleEdit(item)}
                        data-testid={editTestId(item.id)}
                        aria-label={`Edit ${item.name}`}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeleteClick(item)}
                        data-testid={deleteTestId(item.id)}
                        aria-label={`Delete ${item.name}`}
                        className="text-destructive hover:bg-destructive/10"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Form Dialog — dispatch to the custom restaurant form when applicable. */}
      {config.customForm && libraryName === "restaurants" ? (
        <RestaurantFormDialog
          open={formOpen}
          onOpenChange={setFormOpen}
          editingItem={editingItem}
          onSuccess={handleFormSuccess}
        />
      ) : (
        <LibraryFormDialog
          open={formOpen}
          onOpenChange={setFormOpen}
          libraryName={libraryName}
          config={config}
          editingItem={editingItem}
          onSuccess={handleFormSuccess}
          materialsLib={materialsLib}
          liveStationsLib={liveStationsLib}
          standardStationsLib={standardStationsLib}
          productionAreasLib={productionAreasLib}
          staffLib={staffLib}
          specialEventGroupsLib={specialEventGroupsLib}
          canapesLib={canapesLib}
        />
      )}

      <DeleteConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        itemName={deletingItem?.name}
        loading={deleteLoading}
        onConfirm={handleDeleteConfirm}
      />

      {/* Phase 15 (F2) — Import-from-Live-Stations picker. Only
          mounted on the Crew Mess Live Stations page. */}
      {libraryName === "crew_mess_live_stations" && (
        <ImportFromLiveStationsDialog
          open={importFromLSOpen}
          onOpenChange={setImportFromLSOpen}
          onImported={() => {
            // Refresh the table so the imported row appears immediately.
            fetchItems();
          }}
        />
      )}
    </div>
  );
}
